import React, { useEffect } from "react";
import * as d3 from "d3";

export function StatusAnalyticsChart({ filteredData }) {
  useEffect(() => {
    // Select the chart container and measure its width
    const container = d3.select("#chart");
    // If container is not yet in the DOM, bail out
    if (!container.node()) return;
    
    const containerWidth = container.node().getBoundingClientRect().width;
    // You can keep a fixed height or base it on containerWidth for aspect ratio
    const fullHeight = 400;
    
    // Margins
    const margin = { top: 20, right: 30, bottom: 40, left: 50 };
    // Compute the chart drawing area
    const width = containerWidth - margin.left - margin.right;
    const height = fullHeight - margin.top - margin.bottom;

    // Clear any existing content
    container.selectAll("*").remove();

    // Create SVG
    const svg = container
      .append("svg")
      .attr("width", containerWidth) // fill parent width
      .attr("height", fullHeight)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    /**
     * Prepare the data:
     * Make an array of length 24 (for each hour),
     * count how many pings happen in each hour.
     */
    const hourData = d3.range(24).map((hour) => {
      const items = filteredData.filter((d) => new Date(d.analytics_date).getHours() === hour);
      return {
        hour,
        count: items.length,
        hasData: items.length > 0,
      };
    });

    // Determine the max count (for Y scale domain)
    const maxCount = d3.max(hourData, (d) => d.count) || 0;

    // X scale (band scale for hours 0..23)
    const x = d3
      .scaleBand()
      .domain(hourData.map((d) => d.hour))
      .range([0, width])
      .padding(0.1);

    // Y scale (linear scale for count)
    const y = d3
      .scaleLinear()
      .domain([0, maxCount])
      .range([height, 0]);

    // X-axis
    const xAxis = d3.axisBottom(x).tickFormat((d) => `${d}:00`);
    svg
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(xAxis);

    // Y-axis
    const yAxis = d3.axisLeft(y).ticks(5);
    svg.append("g").call(yAxis);

    // X-axis label
    svg
      .append("text")
      .attr("transform", `translate(${width / 2}, ${height + margin.bottom - 5})`)
      .style("text-anchor", "middle")
      .style("font-weight", "bold")
      .text("Hour of the day");

    // Y-axis label
    svg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", -margin.left + 15)
      .attr("x", -height / 2)
      .style("text-anchor", "middle")
      .style("font-weight", "bold")
      .text("Number of pings received");

    // Tooltip
    const tooltip = container
      .append("div")
      .style("position", "fixed")
      .style("visibility", "hidden")
      .style("background", "white")
      .style("border", "1px solid #ebebeb")
      .style("padding", "8px")
      .style("border-radius", "5px")
      .style("font-size", "12px")
      .style("box-shadow", "0px 0px 4px rgba(0,0,0,0.2)");

    // Bars
    svg
      .selectAll(".bar")
      .data(hourData)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", (d) => x(d.hour))
      .attr("y", (d) => (d.hasData ? y(d.count) : 0))
      .attr("width", x.bandwidth())
      .attr("height", (d) => (d.hasData ? height - y(d.count) : height))
      .attr("fill", (d) => (d.hasData ? "#eab308" : "#ebebeb"))
      .on("mouseover", (event, d) => {
        tooltip
          .html(
            d.hasData
              ? `<strong>Hour:</strong> ${d.hour}:00<br/>ping: <strong>${d.count}</strong>`
              : `<strong>Hour:</strong> ${d.hour}:00<br/>No pings`
          )
          .style("visibility", "visible");
      })
      .on("mousemove", (event) => {
        // Adjust the tooltip position as needed
        tooltip
          .style("top", `${event.pageY - 30}px`)
          .style("left", `${event.pageX + 10}px`);
      })
      .on("mouseout", () => {
        tooltip.style("visibility", "hidden");
      });
  }, [filteredData]);

  return (
    <div>
      {/* Chart Container */}
      <div id="chart" className="relative w-full" style={{ minHeight: "400px" }} />
    </div>
  );
}
